import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { GRADE_OPTIONS, encodeGradeValue, decodeGradeValue, RAW_GRADES, RAW_GRADE_META, type GradingCo, type GradeVariant, type RawGrade } from "@/lib/marketplace";
import { PRIMARY_TCGS, POPULAR_TCGS, POPULAR_SPORTS, OTHER_SPORTS, isSportLabel } from "@/lib/tcg-catalog";
import { CARD_LANGUAGES, type CardLanguage, languageLabel } from "@/lib/card-language";
import { useRef, useState } from "react";
import { Lock, Eye, ImagePlus, X, Truck, Camera, Star, Plus, Trash2 } from "lucide-react";

import { CarrierSelect } from "@/components/CarrierSelect";
import { createBuyOrder } from "@/lib/buy-order-create.functions";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { GuidePriceIndicator } from "@/components/GuidePriceIndicator";
import { useCurrency } from "@/lib/currency";


const TCG_ENUM_MAP: Record<string, "pokemon" | "one_piece" | "mtg" | "yugioh" | "lorcana" | "digimon" | "other"> = {
  "Pokémon": "pokemon",
  "One Piece": "one_piece",
  "Magic: The Gathering": "mtg",
  "Yu-Gi-Oh!": "yugioh",
  "Yu-Gi-Oh": "yugioh",
  "Disney Lorcana": "lorcana",
  "Lorcana": "lorcana",
  "Digimon": "digimon",
  "Other": "other",
};

const createOrderSearchSchema = z.object({
  tcg: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/create-order")({
  validateSearch: zodValidator(createOrderSearchSchema),
  head: () => ({ meta: [{ title: "Post a buy order — Want To Buy" }] }),
  component: CreateOrder,
});

function CreateOrder() {
  const navigate = useNavigate();
  const createBuyOrderFn = useServerFn(createBuyOrder);
  const searchParams = Route.useSearch();
  const initialTcg = (searchParams.tcg as string | undefined) ?? PRIMARY_TCGS[0];
  const [tcgLabel, setTcgLabel] = useState<string>(initialTcg);
  const [cardName, setCardName] = useState("");
  const [setName, setSetName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  
  const [customRequests, setCustomRequests] = useState("");
  const [condition, setCondition] = useState<"raw" | "graded">("graded");
  const [rawGrade, setRawGrade] = useState<RawGrade>("NM");
  const [cardLanguage, setCardLanguage] = useState<CardLanguage | "">("");
  const [grades, setGrades] = useState<{ id: number; gradingCo: GradingCo | "Other"; minGrade: number; variant?: GradeVariant; customCo?: string }[]>([
    { id: 1, gradingCo: "PSA", minGrade: 9 },
  ]);
  const [primaryId, setPrimaryId] = useState<number>(1);
  const [nextGradeId, setNextGradeId] = useState(2);
  const [target, setTarget] = useState(500);
  
  const [autoAccept, setAutoAccept] = useState<number | "">("");
  const [maxCeiling, setMaxCeiling] = useState<number | "">("");
  const [buyerCity, setBuyerCity] = useState<string>("");
  const [preferredCarrier, setPreferredCarrier] = useState<string>("");
  const [priceIncludesShipping, setPriceIncludesShipping] = useState<boolean | null>(null);
  const [allowOffers, setAllowOffers] = useState<boolean>(false);
  


  const [refImage, setRefImage] = useState<string | null>(null);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  


  function addGrade() {
    const used = new Set(grades.map(g => g.gradingCo));
    const next = (["PSA", "BGS", "CGC"] as GradingCo[]).find(c => !used.has(c)) ?? "PSA";
    setGrades([...grades, { id: nextGradeId, gradingCo: next, minGrade: 9 }]);
    setNextGradeId(nextGradeId + 1);
  }
  function removeGrade(id: number) {
    const next = grades.filter(g => g.id !== id);
    setGrades(next);
    if (primaryId === id && next.length) setPrimaryId(next[0].id);
  }

  function validatePricing(): string | null {
    if (target <= 0) return "Target price must be greater than 0.";
    if (autoAccept !== "" && maxCeiling !== "" && autoAccept > maxCeiling) return "Auto-accept must be ≤ Max ceiling.";
    if (maxCeiling !== "" && maxCeiling < target) return "Max ceiling must be ≥ Target.";
    if (autoAccept !== "" && autoAccept <= 0) return "Auto-accept must be greater than 0.";
    if (maxCeiling !== "" && maxCeiling <= 0) return "Max ceiling must be greater than 0.";
    return null;
  }

  function buildConditionMin(): string {
    if (condition === "raw") return rawGrade;
    const primary = grades.find(g => g.id === primaryId) ?? grades[0];
    if (!primary) return "PSA 9";
    const co = primary.gradingCo === "Other" ? (primary.customCo?.trim() || "Other") : primary.gradingCo;
    const variant = primary.variant ? ` ${primary.variant}` : "";
    return `${co} ${primary.minGrade}${variant}`;
  }

  function buildDescription(): string {
    const lines: string[] = [];
    if (setName.trim()) lines.push(`Set: ${setName.trim()}`);
    if (cardNumber.trim()) lines.push(`Card #: ${cardNumber.trim()}`);
    lines.push(`Target: $${target}`);
    if (autoAccept !== "") lines.push(`Auto-accept (hidden): $${autoAccept}`);
    if (maxCeiling !== "") lines.push(`Max ceiling (hidden): $${maxCeiling}`);
    
    if (buyerCity.trim()) lines.push(`Buyer city: ${buyerCity.trim()}`);
    if (preferredCarrier.trim()) lines.push(`Preferred carrier: ${preferredCarrier.trim()}`);
    if (condition === "graded" && grades.length) {
      const formatted = grades.map(g => {
        const co = g.gradingCo === "Other" ? (g.customCo?.trim() || "Other") : g.gradingCo;
        const v = g.variant ? ` ${g.variant}` : "";
        const star = g.id === primaryId && grades.length > 1 ? " (primary)" : "";
        return `${co} ${g.minGrade}${v}${star}`;
      }).join(", ");
      lines.push(`Accepted grades: ${formatted}`);
    }
    return lines.join("\n");

  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      navigate({ to: "/auth", search: { redirect: "/create-order" } });
      return;
    }
    const err = validatePricing();
    if (err) { setPricingError(err); return; }
    if (condition === "graded" && grades.length > 1 && !grades.some(g => g.id === primaryId)) {
      setPricingError("Please mark one grade as the Primary Request.");
      return;
    }
    if (!cardName.trim()) { setPricingError("Card name is required."); return; }
    if (!buyerCity.trim()) { setPricingError("Buyer city is required."); return; }
    if (priceIncludesShipping === null) { setPricingError("Please indicate whether your price includes shipping."); return; }
    setPricingError(null);


    const isSport = isSportLabel(tcgLabel);
    const tcg = isSport ? "other" : (TCG_ENUM_MAP[tcgLabel] ?? "other");
    const sport = isSport ? tcgLabel : null;
    const titleParts = [`WTB ${cardName.trim()}`];
    if (setName.trim()) titleParts.push(`— ${setName.trim()}`);
    if (cardNumber.trim()) titleParts.push(`#${cardNumber.trim()}`);
    const title = titleParts.join(" ").slice(0, 200);

    setSubmitting(true);
    try {
      let referenceImagePath: string | undefined;
      if (refFile && user?.id) {
        const ext = (refFile.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 5) || "jpg";
        const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("buy-order-images")
          .upload(path, refFile, { contentType: refFile.type || "image/jpeg", upsert: false });
        if (upErr) {
          toast.error(`Image upload failed: ${upErr.message}`);
          setSubmitting(false);
          return;
        }
        referenceImagePath = path;
      }
      const { id } = await createBuyOrderFn({
        data: {
          tcg,
          sport,
          title,
          cardName: cardName.trim(),
          conditionType: condition,
          conditionMin: buildConditionMin(),
          cardLanguage: cardLanguage || null,
          maxPriceCents: Math.round(target * 100),
          currency: "USD",
          quantity: 1,
          description: buildDescription(),
          customRequests: customRequests.trim() || undefined,
          referenceImagePath,
          priceIncludesShipping: priceIncludesShipping ?? false,
          allowOffers,
        },

      });
      toast.success("Buy order posted");
      navigate({ to: "/orders/$id", params: { id } });
    } catch (e2) {
      toast.error(e2 instanceof Error ? e2.message : "Failed to post order");
      setSubmitting(false);
    }
  }



  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10MB or smaller");
      return;
    }
    setRefFile(f);
    setRefImage(URL.createObjectURL(f));
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-3xl font-bold">Post a buy order</h1>
        <p className="mt-1 text-sm text-muted-foreground">Tell sellers exactly what you want and what you'll pay.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          <section className="panel space-y-4 p-5">
            <h2 className="font-display text-lg font-semibold">Card details</h2>
            <Field label="Category">
              <select className="input-base" value={tcgLabel} onChange={(e) => setTcgLabel(e.target.value)}>
                <optgroup label="Primary Games">
                  {PRIMARY_TCGS.map((t) => <option key={t} value={t}>{t}</option>)}
                </optgroup>
                <optgroup label="More Games">
                  {POPULAR_TCGS.map((t) => <option key={t} value={t}>{t}</option>)}
                </optgroup>
                <optgroup label="Sports — Popular">
                  {POPULAR_SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </optgroup>
                <optgroup label="Sports — All Others">
                  {OTHER_SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </optgroup>
                <optgroup label="Other">
                  <option value="Other">Other</option>
                </optgroup>
              </select>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Card name"><input className="input-base" placeholder={isSportLabel(tcgLabel) ? "e.g. Mike Trout" : "e.g. Charizard"} value={cardName} onChange={(e) => setCardName(e.target.value)} required /></Field>
              <Field label={isSportLabel(tcgLabel) ? "Brand & year" : "Set"}>
                <input
                  className="input-base"
                  placeholder={isSportLabel(tcgLabel) ? "e.g. 2023 Topps Chrome" : "e.g. Base Set"}
                  value={setName}
                  onChange={(e) => setSetName(e.target.value)}
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_1.4fr]">
              <Field label="Card number"><input className="input-base" placeholder="e.g. 4/102" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} /></Field>

              <Field label="Condition">
                <div className="grid grid-cols-2 gap-2">
                  {(["graded", "raw"] as const).map(c => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setCondition(c)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold capitalize transition ${
                        condition === c ? "border-primary bg-primary/10 text-primary" : "border-border bg-background/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >{c}</button>
                  ))}
                </div>
              </Field>
            </div>
            {condition === "graded" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Grading preferences {grades.length > 1 && <span className="ml-1 text-[10px] normal-case text-muted-foreground/80">— mark one as primary</span>}
                  </span>
                  {grades.length < 3 && (
                    <button type="button" onClick={addGrade} className="inline-flex items-center gap-1 rounded-md border border-border bg-background/40 px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-primary hover:border-primary/60">
                      <Plus className="h-3 w-3" /> Add grading
                    </button>
                  )}
                </div>
                {grades.map((g) => {
                  const isPrimary = primaryId === g.id;
                  const multi = grades.length > 1;
                  return (
                    <div key={g.id} className={`rounded-md border p-3 ${isPrimary && multi ? "border-primary/60 bg-primary/5" : "border-border bg-background/40"}`}>
                      {multi && (
                        <div className="mb-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setPrimaryId(g.id)}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isPrimary ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                            title="Set as primary request"
                          >
                            <Star className={`h-3.5 w-3.5 ${isPrimary ? "fill-primary text-primary" : ""}`} />
                            {isPrimary ? "Primary request" : "Set as primary"}
                          </button>
                          <button type="button" onClick={() => removeGrade(g.id)} className="text-muted-foreground hover:text-destructive" title="Remove">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Grading company</span>
                          <select
                            className="input-base"
                            value={g.gradingCo}
                            onChange={(e) => {
                              const co = e.target.value as GradingCo | "Other";
                              // Reset variant when switching companies — sub-grades aren't transferable.
                              setGrades(grades.map(x => x.id === g.id ? { ...x, gradingCo: co, variant: undefined, minGrade: 9, customCo: co === "Other" ? (x.customCo ?? "") : undefined } : x));
                            }}
                          >
                            <option>PSA</option><option>BGS</option><option>CGC</option><option>Other</option>
                          </select>
                          {g.gradingCo === "Other" && (
                            <input
                              type="text"
                              placeholder="Company name (e.g. SGC, HGA, TAG)"
                              className="input-base mt-2"
                              value={g.customCo ?? ""}
                              onChange={(e) => setGrades(grades.map(x => x.id === g.id ? { ...x, customCo: e.target.value } : x))}
                            />
                          )}
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Minimum acceptable grade</span>
                          {g.gradingCo === "Other" ? (
                            <input
                              type="number"
                              step="0.5"
                              min={1}
                              max={10}
                              className="input-base"
                              value={g.minGrade}
                              onChange={(e) => setGrades(grades.map(x => x.id === g.id ? { ...x, minGrade: Number(e.target.value) } : x))}
                            />
                          ) : (
                            <select
                              className="input-base"
                              value={encodeGradeValue(g.minGrade, g.variant)}
                              onChange={(e) => {
                                const { grade, variant } = decodeGradeValue(e.target.value);
                                setGrades(grades.map(x => x.id === g.id ? { ...x, minGrade: grade, variant } : x));
                              }}
                            >
                              {GRADE_OPTIONS[g.gradingCo as GradingCo].map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          )}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                <Field label="Condition grade (required)">
                  <div className="grid grid-cols-5 gap-2">
                    {RAW_GRADES.map(g => (
                      <button
                        type="button"
                        key={g}
                        onClick={() => setRawGrade(g)}
                        title={RAW_GRADE_META[g].full}
                        className={`rounded-md border px-2 py-2 text-xs font-bold transition ${
                          rawGrade === g ? RAW_GRADE_META[g].tone : "border-border bg-background/40 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <div>{g}</div>
                        <div className="mt-0.5 text-[9px] font-medium opacity-80">{RAW_GRADE_META[g].full}</div>
                      </button>
                    ))}
                  </div>
                </Field>
                <p className="text-xs text-muted-foreground">
                  Not sure how grades are defined? See the{" "}
                  <a href="/condition-guide" target="_blank" rel="noreferrer" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
                    Want To Buy Condition Guide
                  </a>{" "}
                  — the official standard used for raw card dispute resolution (NM = PSA 8+, LP = PSA 6–7, MP = PSA 4–5, HP = PSA 2–3, DMG = PSA 1).
                </p>
                <p className="text-xs text-muted-foreground">
                  For raw cards, sellers must submit 2 timestamped photos (front and back, with a handwritten note showing date, both usernames, order ID, and condition) before you're asked to connect — this applies to every order regardless of grade or price.
                </p>

              </div>
            )}

            <Field label="Language (optional)">
              <select
                className="input-base"
                value={cardLanguage}
                onChange={(e) => setCardLanguage(e.target.value as CardLanguage | "")}
              >
                <option value="">Any language</option>
                {CARD_LANGUAGES.map((l) => (
                  <option key={l} value={l}>{languageLabel(l)}</option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Leave as "Any language" unless you need a specific print (e.g. Japanese original).
              </p>
            </Field>
          </section>

          <section className="panel space-y-3 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-semibold">Reference image (optional)</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Helps sellers match the exact card you want. A watermark is added automatically — your image is never used as proof of the seller's card.
                </p>
              </div>
              {refImage && (
                <button
                  type="button"
                  onClick={() => { setRefImage(null); setRefFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="btn-ghost inline-flex items-center gap-1 px-2 py-1 text-xs"
                ><X className="h-3 w-3" /> Remove</button>
              )}
            </div>

            <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />

            {!refImage ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-sm text-muted-foreground transition hover:border-primary/60 hover:text-primary"
              >
                <ImagePlus className="h-6 w-6" />
                <span>Click to upload a reference photo</span>
                <span className="text-xs">PNG or JPG, up to 10MB</span>
              </button>
            ) : (
              <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-md border border-border bg-background/40">
                <img src={refImage} alt="Reference" className="block h-auto w-full object-contain" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/70 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm sm:text-xs">
                  For reference only — seller must submit verified photos
                </div>
                <div className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/90">
                  Buyer reference
                </div>
              </div>
            )}
          </section>

          <section className="panel space-y-4 p-5">
            <div>
              <h2 className="font-display text-lg font-semibold">Pricing</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                All buy orders are posted and settled in <span className="font-semibold text-foreground">USD</span>. If you've selected another display currency, we'll show an approximate conversion for reference only.
              </p>
            </div>

            {/* Primary row: Target price + Allow Offers toggle side by side. */}
            <div className="grid gap-3 sm:grid-cols-2 sm:items-stretch">
              <PriceField
                icon={<Eye className="h-4 w-4 text-primary" />}
                label="Target (public, USD)"
                hint="Visible to sellers."
                value={target}
                onChange={(n) => setTarget(n === "" ? 0 : n)}
              />
              <label className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowOffers}
                  onChange={(e) => setAllowOffers(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <div className="flex-1">
                  <div className="text-xs font-semibold">
                    Allow negotiated offers
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {allowOffers
                      ? "Sellers can send offers below or at your target. Multiple offers stay open until you approve a Purchase Confirmation from one seller."
                      : "Off (default) — fixed-price order. Sellers can only fulfill at your target price. The first seller to click Fulfill locks the order."}
                  </p>
                </div>
              </label>
            </div>

            {/* Auto-accept / Max ceiling only apply when negotiated offers are allowed. */}
            {allowOffers && (
              <div className="grid gap-3 sm:grid-cols-2 animate-fade-in">
                <PriceField
                  icon={<Lock className="h-4 w-4 text-success" />}
                  label="Auto-accept (hidden, USD)"
                  hint="Optional. Any offer at or below this amount is auto-accepted. Can be set above your public Target if you're willing to auto-pay a premium for instant fulfillment — must stay ≤ Max ceiling."
                  value={autoAccept}
                  onChange={setAutoAccept}
                  optional
                  placeholder="Leave blank"
                />
                <PriceField
                  icon={<Lock className="h-4 w-4 text-destructive" />}
                  label="Max ceiling (hidden, USD)"
                  hint="Optional. Offers above this price are auto-rejected."
                  value={maxCeiling}
                  onChange={setMaxCeiling}
                  optional
                  placeholder="Leave blank"
                />
              </div>
            )}

            {pricingError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {pricingError}
              </div>
            )}

            <GuidePriceIndicator cardName={cardName} conditionMin={buildConditionMin()} target={target} />
          </section>



          <section className="panel space-y-4 p-5">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Shipping &amp; location</h2>
            <Field label="Buyer city">
              <input
                required
                type="text"
                value={buyerCity}
                onChange={(e) => setBuyerCity(e.target.value)}
                placeholder="e.g., Montreal, QC"
                className="input-base"
              />
              <span className="mt-1 block text-[11px] text-muted-foreground">
                Shown publicly on your listing so sellers can quote a shipping estimate.
              </span>
            </Field>
            <Field label="Preferred carrier (optional)">
              <CarrierSelect value={preferredCarrier} onChange={setPreferredCarrier} includeNone />
              <span className="mt-1 block text-[11px] text-muted-foreground">
                Shown to sellers as a suggestion when they submit a fulfillment offer. Not binding.
              </span>
            </Field>
            <Field label="My offer price includes shipping">
              <div className="grid grid-cols-2 gap-2">
                {([
                  { v: true, l: "Yes — shipping is included" },
                  { v: false, l: "No — seller adds shipping" },
                ] as const).map((opt) => {
                  const active = priceIncludesShipping === opt.v;
                  return (
                    <button
                      type="button"
                      key={String(opt.v)}
                      onClick={() => setPriceIncludesShipping(opt.v)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition ${
                        active ? "border-primary bg-primary/10 text-primary" : "border-border bg-background/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>
              <span className="mt-1 block text-[11px] text-muted-foreground">
                Sellers see this on your order so they know whether to factor shipping into their offer.
              </span>
            </Field>
          </section>


          <section className="panel space-y-2 p-5">
            <h2 className="font-display text-lg font-semibold">Custom requests (optional)</h2>
            <p className="text-[11px] text-muted-foreground">
              Spell out any specific requirements — centering preference, must be sleeved, no whitening, specific print run, language, first-edition only, etc. Sellers see this before making an offer.
            </p>
            <textarea
              placeholder={"e.g. Centering must be 55/45 or better on front. No whitening on back edges. Prefer 1st edition."}
              className="input-base !h-auto min-h-[96px] py-2"
              value={customRequests}
              onChange={(e) => setCustomRequests(e.target.value)}
              maxLength={2000}
            />
            <div className="text-right text-[10px] text-muted-foreground">{customRequests.length}/2000</div>
          </section>


          <section className="panel space-y-3 p-5">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" /> Buyer protection notice
            </h2>
            <p className="text-xs text-muted-foreground">
              Before you connect with a seller, they're required to submit <span className="text-foreground font-semibold">2 timestamped photos</span> (front and back, with a handwritten note showing date, both usernames, order ID, and condition) — for <span className="text-foreground font-semibold">every order, regardless of price or condition</span>. Review these before approving to confirm the card matches what you expect.
            </p>
            <p className="text-xs text-muted-foreground">
              For <span className="text-foreground font-semibold">graded cards</span>, the seller includes the cert number in the photos so you can verify it independently on PSA/BGS/CGC.
            </p>
            <p className="text-xs text-muted-foreground">
              Payment happens directly between you and the seller. Want To Buy <span className="text-foreground font-semibold">never holds, controls, or processes funds</span> — we strongly recommend <span className="text-foreground font-semibold">PayPal Goods &amp; Services</span>, since it's the only method that gives you dispute protection if something goes wrong.
            </p>
            <p className="text-xs text-muted-foreground">
              If there's an issue, open a dispute directly through PayPal. Want To Buy can't mediate, but we'll support your case with your approved photos and message thread as evidence.
            </p>
          </section>



          <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-sm disabled:opacity-60">{submitting ? "Posting…" : user ? "Post buy order" : "Sign in to post buy order"}</button>
        </form>
      </div>

      <style>{`.input-base { width:100%; height:2.5rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.75rem; font-size: 0.875rem; color: var(--color-foreground); }
      .input-base:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in oklab, var(--color-primary) 30%, transparent); }`}</style>
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function PriceField({ icon, label, hint, value, onChange, optional, placeholder }: { icon: React.ReactNode; label: string; hint: string; value: number | ""; onChange: (n: number | "") => void; optional?: boolean; placeholder?: string }) {
  const { display, format } = useCurrency();
  const numeric = typeof value === "number" ? value : 0;
  const showConversion = display !== "USD" && numeric > 0;
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
        {icon}
        {label}
        {optional && <span className="ml-1 text-[10px] font-normal uppercase tracking-wider text-muted-foreground">Optional</span>}
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">{hint}</p>
      <div className="flex items-center gap-1 rounded-md border border-border bg-background/60 px-2 focus-within:border-primary/60">
        <span className="text-muted-foreground text-sm">$</span>
        <input
          type="number"
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? "" : Number(v));
          }}
          className="flex-1 bg-transparent py-2 text-sm outline-none"
        />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">USD</span>
      </div>
      <div className="mt-1 min-h-[14px] text-[11px] text-muted-foreground" title="Display only — orders are settled in USD">
        {showConversion ? `≈ ${format(numeric)}` : ""}
      </div>
    </div>
  );
}
